import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchApartmentAddressOptions, fetchOnSaleApartments } from './api';
import { ApartmentAddressApartment } from './types';
import { IProjectApartment } from '../projects/project/data/entities';
import notFound from './assets/address_notfound.png';
import addressSearch from './assets/address_search.png';

interface IProps {
  projectPlanId: number;
  announcementId?: number;
  projectName?: string;
  onApartmentClick?: (apartment: ApartmentAddressApartment) => void;
}

type Option = { label: string; value: string };
interface AddressResultRow extends ApartmentAddressApartment {
  building: string;
  entrance: string;
  floor: string;
  roomCount?: string;
  internalSize?: string;
  price?: string;
}

/* =========================================================
   Icons
   ========================================================= */
const CaretDownIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M2 4.5 6 8.5l4-4"
      stroke="#6C757D"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface SelectFieldProps {
  value: string;
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

const SelectField: React.FC<SelectFieldProps> = ({
  value,
  options,
  placeholder = 'Seçin',
  onChange,
  disabled,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div
      className={`asp-select ${open ? 'asp-select--open' : ''} ${disabled ? 'asp-select--disabled' : ''
        } ${error ? 'asp-select--error' : ''}`}
      ref={ref}
    >
      <button
        type="button"
        className="asp-select__trigger"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span
          className={`asp-select__value ${!selected ? 'asp-select__value--placeholder' : ''
            }`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <CaretDownIcon />
      </button>
      {open && options.length > 0 && (
        <ul className="asp-select__menu" role="listbox">
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`asp-select__option ${opt.value === value ? 'asp-select__option--active' : ''
                }`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const toOpts = (nums: number[]): Option[] =>
  [...nums].sort((a, b) => a - b).map((n) => ({ value: String(n), label: String(n) }));

export const AddressSearchPanel: React.FC<IProps> = ({
  projectPlanId,
  announcementId,
  projectName,
  onApartmentClick,
}) => {
  const [buildings, setBuildings] = useState<number[]>([]);
  const [entrances, setEntrances] = useState<number[]>([]);
  const [floors, setFloors] = useState<number[]>([]);
  const [apartments, setApartments] = useState<ApartmentAddressApartment[]>([]);

  const [building, setBuilding] = useState('');
  const [entrance, setEntrance] = useState('');
  const [floor, setFloor] = useState('');
  const [apartment, setApartment] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [onSaleApartments, setOnSaleApartments] = useState<IProjectApartment[]>([]);

  const [results, setResults] = useState<AddressResultRow[]>([]);

  const [errors, setErrors] = useState({
    building: false,
    entrance: false,
    floor: false,
    apartment: false,
  });

  /* Fetch buildingNumbers on mount / when announcement or plan changes. */
  useEffect(() => {
    if (!projectPlanId) {
      console.log('[AddressSearch] skip buildings fetch — no projectPlanId');
      return;
    }
    console.log('[AddressSearch] → buildings', { projectPlanId, announcementId });
    fetchApartmentAddressOptions({ projectPlanId, announcementId })
      .then((r) => {
        console.log('[AddressSearch] ← buildings response', r);
        setBuildings(r.buildingNumbers ?? []);
      })
      .catch((err) => {
        console.error('[AddressSearch] ✕ buildings failed', err);
        setBuildings([]);
      });
  }, [projectPlanId, announcementId]);

  useEffect(() => {
    if (!projectPlanId) {
      setOnSaleApartments([]);
      return;
    }
    let cancelled = false;
    fetchOnSaleApartments(projectPlanId, announcementId).then((apts) => {
      if (!cancelled) setOnSaleApartments(apts);
    });
    return () => {
      cancelled = true;
    };
  }, [projectPlanId, announcementId]);

  /* Lookup keyed by building:entrance:floor:apartmentNumber for O(1)
     enrichment of each result row. */
  const onSaleLookup = useMemo(() => {
    const m = new Map<string, IProjectApartment>();
    for (const a of onSaleApartments) {
      const key = `${Number(a.buildingNumber)}:${Number(a.entrance)}:${Number(
        a.floorNumber,
      )}:${Number(a.apartmentNumber)}`;
      m.set(key, a);
    }
    return m;
  }, [onSaleApartments]);

  /* Fetch entranceNumbers when a building is picked. */
  useEffect(() => {
    if (!projectPlanId || !building) {
      setEntrances([]);
      return;
    }
    console.log('[AddressSearch] → entrances', {
      projectPlanId,
      announcementId,
      buildingNumber: building,
    });
    fetchApartmentAddressOptions({
      projectPlanId,
      announcementId,
      buildingNumber: building,
    })
      .then((r) => {
        console.log('[AddressSearch] ← entrances response', r);
        setEntrances(r.entranceNumbers ?? []);
      })
      .catch((err) => {
        console.error('[AddressSearch] ✕ entrances failed', err);
        setEntrances([]);
      });
  }, [projectPlanId, announcementId, building]);

  /* Fetch floorNumbers when an entrance is picked. */
  useEffect(() => {
    if (!projectPlanId || !building || !entrance) {
      setFloors([]);
      return;
    }
    console.log('[AddressSearch] → floors', {
      projectPlanId,
      announcementId,
      buildingNumber: building,
      entranceNumber: entrance,
    });
    fetchApartmentAddressOptions({
      projectPlanId,
      announcementId,
      buildingNumber: building,
      entranceNumber: entrance,
    })
      .then((r) => {
        console.log('[AddressSearch] ← floors response', r);
        setFloors(r.floorNumbers ?? []);
      })
      .catch((err) => {
        console.error('[AddressSearch] ✕ floors failed', err);
        setFloors([]);
      });
  }, [projectPlanId, announcementId, building, entrance]);

  /* Fetch apartments when a floor is picked. */
  useEffect(() => {
    if (!projectPlanId || !building || !entrance || !floor) {
      setApartments([]);
      return;
    }
    console.log('[AddressSearch] → apartments', {
      projectPlanId,
      announcementId,
      buildingNumber: building,
      entranceNumber: entrance,
      floorNumber: floor,
    });
    fetchApartmentAddressOptions({
      projectPlanId,
      announcementId,
      buildingNumber: building,
      entranceNumber: entrance,
      floorNumber: floor,
    })
      .then((r) => {
        console.log('[AddressSearch] ← apartments response', r);
        setApartments(r.apartments ?? []);
      })
      .catch((err) => {
        console.error('[AddressSearch] ✕ apartments failed', err);
        setApartments([]);
      });
  }, [projectPlanId, announcementId, building, entrance, floor]);

  useEffect(() => {
    console.log('[AddressSearch] state.buildings =', buildings);
  }, [buildings]);

  /* Reset everything when the plan changes — including the results
     snapshot, which is stale once the project switches. */
  useEffect(() => {
    setBuilding('');
    setEntrance('');
    setFloor('');
    setApartment('');
    setHasSearched(false);
    setResults([]);
    setErrors({ building: false, entrance: false, floor: false, apartment: false });
  }, [projectPlanId]);

  /* Clear dependent fields when an upstream field changes. */
  useEffect(() => {
    setEntrance('');
    setFloor('');
    setApartment('');
  }, [building]);
  useEffect(() => {
    setFloor('');
    setApartment('');
  }, [entrance]);
  useEffect(() => {
    setApartment('');
  }, [floor]);

  useEffect(() => { if (building) setErrors((e) => ({ ...e, building: false })); }, [building]);
  useEffect(() => { if (entrance) setErrors((e) => ({ ...e, entrance: false })); }, [entrance]);
  useEffect(() => { if (floor) setErrors((e) => ({ ...e, floor: false })); }, [floor]);
  useEffect(() => { if (apartment) setErrors((e) => ({ ...e, apartment: false })); }, [apartment]);

  function reset() {
    setBuilding('');
    setEntrance('');
    setFloor('');
    setApartment('');
    setErrors({ building: false, entrance: false, floor: false, apartment: false });
  }

  function search() {
    const nextErrors = {
      building: !building,
      entrance: !entrance,
      floor: !floor,
      apartment: !apartment,
    };
    setErrors(nextErrors);
    if (nextErrors.building || nextErrors.entrance || nextErrors.floor || nextErrors.apartment) {
      return;
    }

    const matched = apartments.filter(
      (a) => String(a.apartment_number) === apartment,
    );
    const rows: AddressResultRow[] = matched.map((apt) => {
      const key = `${Number(building)}:${Number(entrance)}:${Number(floor)}:${Number(
        apt.apartment_number,
      )}`;
      const info = onSaleLookup.get(key);
      return {
        id: apt.id,
        apartment_number: apt.apartment_number,
        building,
        entrance,
        floor,
        roomCount: info?.roomCount,
        internalSize: info?.internalSize,
        price: info?.price,
      };
    });
    setResults(rows);
    setHasSearched(true);
  }

  const apartmentOptions: Option[] = [...apartments]
    .sort((a, b) => Number(a.apartment_number) - Number(b.apartment_number))
    .map((a) => ({
      value: String(a.apartment_number),
      label: String(a.apartment_number),
    }));

  const showResults = results.length > 0;
  const showNotFound = hasSearched && results.length === 0;
  const showInitial = !showResults && !showNotFound;

  return (
    <div className="asp">
      <aside className="asp__form">
        <h3 className="asp__form-title">{projectName ?? ''}</h3>

        <div className="asp__fields">
          <div className="asp-field">
            <label className="asp-field__label">Bina nömrəsi</label>
            <SelectField
              value={building}
              options={toOpts(buildings)}
              onChange={setBuilding}
              error={errors.building}
            />
          </div>

          <div className="asp-field">
            <label className="asp-field__label">Giriş nömrəsi</label>
            <SelectField
              value={entrance}
              options={toOpts(entrances)}
              onChange={setEntrance}
              disabled={!building}
              error={errors.entrance}
            />
          </div>

          <div className="asp-field">
            <label className="asp-field__label">Mərtəbə nömrəsi</label>
            <SelectField
              value={floor}
              options={toOpts(floors)}
              onChange={setFloor}
              disabled={!entrance}
              error={errors.floor}
            />
          </div>

          <div className="asp-field">
            <label className="asp-field__label">Mənzil nömrəsi</label>
            <SelectField
              value={apartment}
              options={apartmentOptions}
              onChange={setApartment}
              disabled={!floor}
              error={errors.apartment}
            />
          </div>
        </div>

        <div className="asp__actions">
          <button type="button" className="asp-btn asp-btn--ghost" onClick={reset}>
            Yenilə
          </button>
          <button type="button" className="asp-btn asp-btn--primary" onClick={search}>
            Axtar
          </button>
        </div>
      </aside>

      <div className={`asp__panel${showResults ? ' asp__panel--results' : ''}`}>
        {showInitial && (
          <div className="asp__empty">
            <img src={addressSearch} />
            <p className="asp__empty-text">Ünvan üzrə axtarış edin.</p>
          </div>
        )}

        {showNotFound && (
          <div className="asp__empty">
            <img src={notFound} />
            <p className="asp__empty-text">Ünvan üzrə nəticə tapılmadı.</p>
          </div>
        )}

        {showResults && (
          <div className="asp__results asp__results--address">
            <div className="asp-table-wrap">
              <table className="asp-table">
                <thead>
                  <tr>
                    <th>Bina</th>
                    <th>Giriş</th>
                    <th>Mərtəbə</th>
                    <th>Mənzil</th>
                    <th>Otaq sayı</th>
                    <th>Sahə</th>
                    <th>Qiymət</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => (
                    <tr key={row.id} onClick={() => onApartmentClick?.(row)}>
                      <td>{row.building}</td>
                      <td>{row.entrance}</td>
                      <td>{row.floor}</td>
                      <td>{row.apartment_number}</td>
                      <td>
                        {row.roomCount == null
                          ? '—'
                          : row.roomCount === '0'
                            ? 'Studio'
                            : row.roomCount}
                      </td>
                      <td>{row.internalSize == null ? '—' : `${row.internalSize} m²`}</td>
                      <td>{row.price == null ? '—' : `${row.price} AZN`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressSearchPanel;
